import React, { useEffect, useMemo, useState } from "react";
import { Box, Heading, useColorMode } from "@chakra-ui/react";
import { getThemeClasses } from "../../utils/themeUtils";
import BurnoutDetails from "./BurnoutDetails";
import { doc, getDoc } from "firebase/firestore";
import UserCardGridWithPagination from "./UserCardGridWithPagination";
import { db } from "../../firebase/firebase";

export type User = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  assignedChannels?: Record<string, string[]>;
  assignedProjects?: string[];
  assignedTasks?: {
    id: string;
    name: string;
    taskId: string;
    channelId: string;
    projectId: string;
  }[];
};

interface BurnoutAnalysisProps {
  bg: string;
  color: string;
  organizationId: string;
  users: User[];
}

const BurnoutAnalysisMain: React.FC<BurnoutAnalysisProps> = ({
  bg,
  color,
  organizationId,
  users,
}) => {
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const { colorMode } = useColorMode();
  const isDark = colorMode === "dark";
  const theme = getThemeClasses(isDark);
  const [taskDetails, setTaskDetails] = useState<any[]>([]);
  
  // Flatten all tasks and nested subtasks into a single-level array
  const allTaskDetails = useMemo(() => {
    const result: any[] = [];
    const walk = (node: any) => {
      if (!node) return;
      result.push(node);
      const subs: any[] = Array.isArray(node.subtasks) ? node.subtasks : [];
      subs.forEach(walk);
    };
    taskDetails.forEach(walk);
    return result;
  }, [taskDetails]);

  useEffect(() => {
    const fetchTaskDetails = async (
      taskId: string,
      projectId: string,
      channelId: string,
      userId: string
    ) => {
      try {
        const taskDocRef = doc(
          db,
          `organization/${organizationId}/projects/${projectId}/channels/${channelId}/tasks/${taskId}`
        );
        const snapshot = await getDoc(taskDocRef);

        if (snapshot.exists()) {
          const taskData = snapshot.data();
          if (taskData?.data && Array.isArray(taskData.data)) {
            // Filter the data array based on the task id and return the matched task
            const matchedTask = taskData.data.find(
              (task: any) => task.id === userId
            );
            if (matchedTask) {
              // Prevent adding duplicates by checking if the task already exists
              setTaskDetails((prevDetails) => {
                const taskExists = prevDetails.some(
                  (task) => task.id === matchedTask.id
                );
                if (!taskExists) {
                  return [...prevDetails, matchedTask]; // Add task if it does not exist
                }
                return prevDetails; // Return existing details if the task already exists
              });
            }
          }
        }
      } catch (error) {
        console.error("Error fetching task details:", error);
      }
    };

    // Reset task details when the selected user changes
    setTaskDetails([]);

    // Fetch task details for each task assigned to the selected user
    if (selectedUser && selectedUser.assignedTasks) {
      selectedUser.assignedTasks.forEach((task) => {
        const { taskId, projectId, channelId, id } = task;
        fetchTaskDetails(taskId, projectId, channelId, id); // Fetch task details for each assigned task
      });
    }
  }, [selectedUser, organizationId]); // Depend on selectedUser and organizationId

 // Log task details for debugging

  return (
    <Box bg={bg} color={color} width="100%">
      {/* Header Section */}
      {!selectedUser && (
        <Box
          className={`flex justify-between items-center p-4 ${theme.headerGradient} rounded-md mb-4 border ${theme.border}`}
          mb={4}
          position="relative"
        >
          {/* Decorative overlays in dark mode to match UserTable */}
          {isDark && (
            <>
              <span className={theme.headerOverlayGlow} />
              <span className={theme.headerOverlayTopLine} />
              <span className={theme.headerOverlayBottomGlow} />
              <span className={theme.headerOverlaySweep} />
            </>
          )}
          <Box className="flex flex-col space-y-1">
            <span className={`text-2xl font-bold ${theme.text}`}>
              Burnout Analysis
            </span>
            <span className={`text-xs ${theme.textMuted}`}>
              Start by selecting a user to view their burnout details
            </span>
          </Box>
        </Box>
      )}

      {selectedUser ? (
        <BurnoutDetails
          taskDetails={allTaskDetails}
          setTaskDetails={setTaskDetails}
          organizationId={organizationId}
          user={selectedUser}
          onBack={() => setSelectedUser(null)}
          bg={bg}
          color={color}
        />
      ) : (
        <UserCardGridWithPagination
          users={users}
          onSelectUser={setSelectedUser}
          organizationId={organizationId}
        />
      )}
    </Box>
  );
};

export default BurnoutAnalysisMain;
